import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConfirmDialog } from "../components/ConfirmDialog";

interface IntegrationCard {
  to: string;
  name: string;
  description: string;
}

const INTEGRATIONS: IntegrationCard[] = [
  {
    to: "/settings/square",
    name: "Square",
    description:
      "Mirror our counts into Square and decrement our stock on Square sales. Our inventory is the master.",
  },
  {
    to: "/settings/stripe",
    name: "Stripe",
    description:
      "Decrement our stock on paid Checkout sales and deactivate products on Stripe when they hit zero.",
  },
];

export function Integrations() {
  const api = useApi();
  const { data, loading, error, reload, setData } = useAsync(() => api.getSiteSettings(), [api]);

  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  const devMode = data?.devMode ?? false;
  // The mode we would switch to on confirm (the opposite of the current one).
  const target = !devMode;

  const applyToggle = async () => {
    setSaveError(undefined);
    setSaving(true);
    try {
      const next = await api.updateSiteSettings({ devMode: target });
      setData(() => next);
      setConfirming(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not change the mode.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Integrations</h1>
          <p className="page__subtitle muted">
            Connect sales channels to our inventory. One site-wide switch selects the environment for
            all of them.
          </p>
        </div>
      </div>

      {loading ? (
        <Spinner label="Loading…" />
      ) : error ? (
        <ErrorBanner message={error.message} onRetry={reload} />
      ) : (
        <section className={`panel integration-mode${devMode ? " integration-mode--dev" : ""}`}>
          <div className="panel__header">
            <h2 className="panel__title">Mode</h2>
            {devMode ? (
              <span className="badge badge--warning">Development</span>
            ) : (
              <span className="badge badge--success">Live</span>
            )}
          </div>

          {saveError ? <ErrorBanner title="Change failed" message={saveError} /> : null}

          <p className="muted">
            {devMode ? (
              <>
                <strong>Development mode is on.</strong> Both channels use their test environment —
                Square <strong>Sandbox</strong> and Stripe <strong>Test</strong>. No real sales affect
                stock.
              </>
            ) : (
              <>
                <strong>Live mode.</strong> Both channels use their production environment — Square{" "}
                <strong>Production</strong> and Stripe <strong>Live</strong>. Real customer sales
                decrement our stock.
              </>
            )}
          </p>

          <div className="form-actions form-actions--split">
            <div className="form-actions__group">
              <button
                type="button"
                className={target ? "btn btn--secondary" : "btn btn--primary"}
                onClick={() => setConfirming(true)}
                disabled={saving}
              >
                {target ? "Switch to development mode" : "Switch to live mode"}
              </button>
            </div>
            {data?.updatedAt ? (
              <span className="muted">
                Changed {new Date(data.updatedAt).toLocaleString()}
                {data.updatedBy ? ` by ${data.updatedBy}` : ""}
              </span>
            ) : null}
          </div>
        </section>
      )}

      <div className="detail-grid">
        {INTEGRATIONS.map((integration) => (
          <section key={integration.to} className="panel">
            <div className="panel__header">
              <h2 className="panel__title">{integration.name}</h2>
            </div>
            <p className="muted">{integration.description}</p>
            <div className="form-actions">
              <Link to={integration.to} className="btn btn--secondary btn--sm">
                Configure {integration.name}
              </Link>
            </div>
          </section>
        ))}
      </div>

      {confirming ? (
        <ConfirmDialog
          title={target ? "Switch to development mode?" : "Switch to live mode?"}
          message={
            target
              ? "Both Square and Stripe will switch to their test environment (Square Sandbox + Stripe Test). Make sure the sandbox/test credentials are configured for each. Real sales will stop affecting stock."
              : "Both Square and Stripe will switch to their production environment (Square Production + Stripe Live). Make sure the production/live credentials are configured for each. Real customer sales will decrement our stock immediately."
          }
          confirmLabel={target ? "Switch to development" : "Switch to live"}
          danger={!target}
          busy={saving}
          onConfirm={() => void applyToggle()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}
